import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '@fragments';
import Conversation from './components/Conversation';
import ScriptEditor from './components/ScriptEditor';
import ArtPanel from './components/ArtPanel';
import useRevisionView from './components/ScriptEditor/hooks/useRevisionView';
import Teleprompter from '../../components/Teleprompter';
import MobileDialog from '../../components/MobileDialog';
import { isArtProject } from '../../enums/formats';
import { openImageQuestion } from '../../dialogs/ImageChoiceDialog/format';
import { useStudioSidebarControl } from '../../layout/StudioScreen/context';
import './styles.scss';

// The preview can be closed (the chat then spans the canvas); a script or art card in the
// chat opens it again on its own version, and a run that writes a new result reopens it.
// Manual saves never do: closing mid-autosave keeps it closed. An art project shows its
// art panel from the start, the first generation running inside it.
export default function Workspace({ studio: s }) {
  const { t, revision, editor } = s;
  const art = isArtProject(s.current);
  const [mobileTab, setMobileTab] = useState('chat');
  const [teleprompter, setTeleprompter] = useState(null);
  const [qr, setQr] = useState(false);
  const [closed, setClosed] = useState(false);
  const view = useRevisionView(s);
  const resultId = art ? (s.art.latest?.run_id ? s.art.latest.id : null) : revision?.run_id ? revision.id : null;
  useEffect(() => { if (resultId) setClosed(false); }, [resultId]);
  // The run's image question shows its draft in the art panel, so it opens it too.
  const questionId = art ? openImageQuestion(s.run)?.id : null;
  useEffect(() => { if (questionId) setClosed(false); }, [questionId]);
  const hasPreview = art ? !closed : Boolean(editor && revision && !s.active && !closed);
  // Opening a preview folds the sidebar into its rail: the chat and the preview need the
  // width. The creator can open it again; it folds the next time a preview opens.
  const sidebar = useStudioSidebarControl();
  useEffect(() => { if (hasPreview) sidebar?.collapse(); }, [hasPreview, sidebar]);
  // Stable: the thread and the editor are memoized on these handlers.
  const { show } = view;
  const { view: viewArt } = s.art;
  const openScript = useCallback(id => {
    if (art) viewArt(id); else show(id);
    setClosed(false);
    setMobileTab('script');
  }, [art, show, viewArt]);
  const closePreview = useCallback(() => {
    setClosed(true);
    setMobileTab('chat');
  }, []);
  // "Edit with AI" hands the scoped edit to the chat: on a phone, its tab, then the field.
  const toChat = useCallback(() => {
    setMobileTab('chat');
    requestAnimationFrame(() => document.querySelector('.studio-conversation .studio-composer textarea')?.focus({ preventScroll: true }));
  }, []);
  const openMobile = useCallback(() => setQr(true), []);
  // The shell sizes the art column from the art's proportions and, for a carousel, the room
  // its page arrows take (StudioScreen styles).
  const ratio = art ? s.art.document?.aspect_ratio || s.current.brief?.art?.aspect_ratio || '4:5' : undefined;
  const shown = s.art.viewedDocument || s.art.document;
  const pages = art ? ((shown ? shown.pages.length : s.current.brief?.format === 'instagram::carousel' ? 2 : 1) > 1 ? 'many' : 'one') : undefined;
  return <div className={`studio-workspace ${hasPreview ? '' : 'is-chat-only'} ${art ? 'is-art' : ''}`} data-art-ratio={ratio} data-art-pages={pages}>
    {hasPreview && <div className="studio-workspace__mobile-tabs"><Button color={mobileTab === 'chat' ? 'inverse' : 'overlay'} onClick={() => setMobileTab('chat')}>{t('chat')}</Button><Button color={mobileTab === 'script' ? 'inverse' : 'overlay'} onClick={() => setMobileTab('script')}>{t(art ? 'art' : 'script')}</Button></div>}
    <Conversation studio={s} visible={!hasPreview || mobileTab === 'chat'} onOpenScript={openScript} onTeleprompter={setTeleprompter} />
    {hasPreview && (art
      ? <ArtPanel studio={s} visible={mobileTab === 'script'} onClose={closePreview} onAskAI={toChat} />
      : <ScriptEditor studio={s} view={view} visible={mobileTab === 'script'} onTeleprompter={setTeleprompter} onMobile={openMobile} onClose={closePreview} />)}
    {teleprompter && <Teleprompter script={teleprompter.script} revisionNumber={teleprompter.revision_number} t={t} studio={s} onClose={() => setTeleprompter(null)} />}
    {qr && <MobileDialog studio={s} onClose={() => setQr(false)} />}
  </div>;
}
